import React from 'react';
import './OperatorsSummary.css';
import { useSchoolHours } from './useSchoolHours';

/**
 * Composant OperatorsSummary - Section gauche du tableau mensuel
 * Affiche la liste des opérateurs avec leurs heures mensuelles, heures supp,
 * et jauge de ponctualité
 * + Ligne "Heures de formations" pour les apprentis
 */
export function OperatorsSummary({ operators = [], series = {}, currentMonth, rawData = [] }) {
  // Récupérer les données école
  const { schoolData, loading: schoolLoading } = useSchoolHours(currentMonth);
  
  // 🔥 Calculer les heures mensuelles depuis rawData (données strictes du mois)
  // Plus fiable que series qui peut inclure des débordements de semaines
  const calculateMonthlyHours = (operatorId) => {
    if (!rawData || !Array.isArray(rawData)) return 0;
    
    // Filtrer les lignes de cet opérateur
    const operatorRows = rawData.filter(row => 
      Number(row.operator_id) === Number(operatorId)
    );
    
    // Sommer les work_minutes et convertir en heures
    const totalMinutes = operatorRows.reduce((sum, row) => 
      sum + (Number(row.work_minutes) || 0), 0
    );
    
    return Math.round(totalMinutes / 60);
  };

  // Générer les données réelles à partir des opérateurs
  const realData = Array.isArray(operators) ? operators.map(operator => ({
    id: operator.id,
    name: operator.name,
    monthlyHours: calculateMonthlyHours(operator.id),
    extraHours: 0, // 0 pour l'instant comme demandé
    punctuality: 100 // 100% par défaut comme demandé
  })) : [];

  // Formatage du mois
  const formatMonth = (date) => {
    return new Intl.DateTimeFormat('fr-FR', { 
      year: 'numeric', 
      month: 'long' 
    }).format(date);
  };

  return (
    <div className="operators-summary">
      {/* En-tête avec titre simple */}
      <div className="summary-header">
        <h3 className="detail-title">Détail mensuelle</h3>
      </div>

      {/* Vrai tableau comme AttendanceTable */}
      <div className="operators-table-wrapper">
        <table className="operators-table">
          <thead>
            <tr>
              <th>Opérateur</th>
              <th>Total heure</th>
              <th>Heures supp</th>
              <th>Ponctualité</th>
            </tr>
          </thead>
          <tbody>
            {realData.map((operator) => {
              const hasSchool = schoolData[operator.id];
              const trainingHours = hasSchool ? hasSchool.accumulatedHours : 0;
              const totalHoursWithTraining = operator.monthlyHours + trainingHours;
              
              return (
                <tr key={operator.id} className="table-row">
                  <td className="operator-cell">
                    <span className="operator-name">
                      {operator.name}
                      {hasSchool && <span className="training-badge"> (formation)</span>}
                    </span>
                  </td>
                  <td className="hours-cell">
                    <span className="hours-value">
                      {totalHoursWithTraining}h
                      {hasSchool && <span className="training-hours-inline"> ({trainingHours}h)</span>}
                    </span>
                  </td>
                  <td className="hours-cell">
                    <span className={`hours-value ${operator.extraHours === 0 ? 'zero-hours' : 'overtime-hours'}`}>
                      {operator.extraHours}h
                    </span>
                  </td>
                  <td className="punctuality-cell">
                    <div className="punctuality-gauge">
                      <div className="gauge-bar">
                        <div 
                          className="gauge-fill" 
                          style={{ width: `${operator.punctuality}%` }}
                        ></div>
                      </div>
                      <span className="gauge-value">{operator.punctuality}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}